import { Box, LinearProgress, SxProps, Theme } from '@mui/material';
import Card from '../../Utils/Card';
import { useScreenLargerThan } from '../../../lib/hooks/breakpoint-hooks';
import {
    DataGrid,
    GridColDef,
    GridFilterModel,
    GridPaginationModel,
    GridSortModel,
    GridToolbarContainer
} from '@mui/x-data-grid';
import Link from '../../Utils/Link';
import AuditDatePicker from '../FilterByDatePicker';
import { AuditLogModel } from '../../../lib/models/AuditModel';
import DatagridNoRowsComponent from '../../DatagridNoRowsComponent';
import { useBeforeUnload, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { serializeFilterModel, serializeSortModel } from './serializationHelperFunctions';
import { navMenuService } from '../../../lib//constants/nav-menu-data';
import useIsUserRole from '../../../lib/hooks/use-is-user-role';
import { CustomDateHourCell } from '../../../lib/helper/time-formatter';
import moment from 'moment';

const NO_DATE_AVAILABLE_TEXT = 'N/A';

export type AuditListParams = {
    caseNumber: string;
    startDate: Date;
    endDate: Date;
};

export type AuditListingProps = {
    data: AuditLogModel;
    inputDateProps: InputDateProps;
    loading: boolean;
    paginationModel: GridPaginationModel | undefined;
    onPaginationModelChange: (paginationModel: GridPaginationModel | undefined) => void;
    filterModel: GridFilterModel | undefined;
    onFilterModelChange: (filterModel: GridFilterModel | undefined) => void;
    sortModel: GridSortModel | undefined;
    onSortModelChange: (sortModel: GridSortModel | undefined) => void;
};

export type InputDateProps = {
    startDate: string;
    endDate: string;
    // eslint-disable-next-line
    setStartDate: any;
    // eslint-disable-next-line
    setEndDate: any;
};

export type CustomNavigationProps = {
    clinicalReviewId: string;
    crmId: string;
    caseNumber: string;
    isCosigned: boolean;
};

export type CustomCaseNumberProps = {
    data: CustomNavigationProps;
};

const CustomCaseNumberCell = (data: CustomCaseNumberProps) => {
    return (
        <Box>
            {data.data.clinicalReviewId && data.data.crmId && data.data.isCosigned !== undefined ? (
                <Link
                    sx={{ textDecoration: 'none' }}
                    to={`/search/${data.data.clinicalReviewId}/${data.data.crmId}/${data.data.isCosigned}`}>
                    {data.data.caseNumber}
                </Link>
            ) : (
                data.data.caseNumber
            )}
        </Box>
    );
};

const columns: GridColDef[] = [
    {
        field: 'lastUpdated',
        headerName: 'Last Updated',
        filterable: false,
        minWidth: 90,
        flex: 1,
        renderCell(params) {
            if (!params.value) {
                return NO_DATE_AVAILABLE_TEXT;
            }
            const dateTime = moment(params.value + 'Z')
                .locale('en-US')
                .tz('America/New_York');
            return (
                <CustomDateHourCell
                    timeValue={dateTime.format(`HH:mm:ss [EST]`)}
                    dateValue={dateTime.format('MM/DD/YYYY')}
                />
            );
        }
    },
    {
        field: 'updatedBy',
        type: 'string',
        headerName: 'Assigned to/Modified by',
        minWidth: 200,
        flex: 1
    },
    {
        field: 'caseNumber',
        type: 'string',
        headerName: 'Case Number',
        minWidth: 120,
        flex: 1,
        renderCell: (params) => {
            const rowParams = {
                clinicalReviewId: params.row.clinicalReviewId,
                crmId: params.row.crmId,
                caseNumber: params.row.caseNumber,
                isCosigned: params.row.isCosigned
            };
            return <CustomCaseNumberCell data={rowParams} />;
        }
    },
    {
        field: 'caseCategory',
        type: 'string',
        headerName: 'Case Category',
        minWidth: 150,
        flex: 1
    },
    {
        field: 'caseStage',
        type: 'string',
        headerName: 'Case Stage',
        minWidth: 150,
        flex: 1
    },
    {
        field: 'caseState',
        type: 'string',
        headerName: 'Case State',
        minWidth: 140,
        flex: 1
    }
];

function CustomToolbar({ startDate, setStartDate, endDate, setEndDate }: InputDateProps) {
    const isLargerThanSmall = useScreenLargerThan('sm');

    return (
        <GridToolbarContainer
            sx={
                isLargerThanSmall
                    ? { display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
                    : { display: 'block' }
            }>
            <Card.Title>Search</Card.Title>
            <Box sx={isLargerThanSmall ? { display: 'flex' } : { display: 'inline' }}>
                <AuditDatePicker
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                />
            </Box>
        </GridToolbarContainer>
    );
}

const AuditList = ({
    data,
    loading,
    inputDateProps,
    paginationModel,
    onPaginationModelChange,
    filterModel,
    onFilterModelChange,
    sortModel,
    onSortModelChange
}: AuditListingProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const isLargerThanSmall = useScreenLargerThan('sm');
    const allowedRolesForAudit = useMemo(() => {
        const roles = navMenuService.getRolesOfNavItem('/search');
        return roles?.filter((r) => r !== 'user') || [];
    }, []);
    const isRoleAllowed = useIsUserRole(...allowedRolesForAudit);

    useEffect(() => {
        sessionStorage.removeItem('auditListUrl');
    }, []);

    useBeforeUnload(() => {
        sessionStorage.setItem(
            'auditListUrl',
            `${serializeFilterModel(filterModel)}:${serializeSortModel(sortModel)}`
        );
    });

    useEffect(() => {
        navigate(
            { hash: location.hash, search: searchParams.toString() },
            { replace: true, state: location.state }
        );
    }, [sortModel, filterModel, location.hash, location.state, navigate, searchParams]);

    return (
        <Card sx={cardStyle(isLargerThanSmall)}>
            <LinearProgress sx={{ visibility: loading ? 'visible' : 'hidden', height: '3px' }} />
            <Card.Body>
                <DataGrid
                    sx={{ border: 0, padding: 1 }}
                    rows={data.data}
                    rowCount={data.total}
                    columns={
                        isRoleAllowed ? columns : columns.filter((x) => x.field !== 'caseCategory')
                    }
                    autoHeight
                    paginationMode="server"
                    paginationModel={paginationModel}
                    pageSizeOptions={[10, 25, 50]}
                    onPaginationModelChange={onPaginationModelChange}
                    slots={{ toolbar: CustomToolbar, noRowsOverlay: DatagridNoRowsComponent }}
                    getRowId={(row) => `${row.clinicalReviewId}_${row.caseStage}`}
                    slotProps={{
                        toolbar: {
                            startDate: inputDateProps.startDate,
                            setStartDate: inputDateProps.setStartDate,
                            endDate: inputDateProps.endDate,
                            setEndDate: inputDateProps.setEndDate
                        }
                    }}
                    filterModel={filterModel}
                    onFilterModelChange={onFilterModelChange}
                    filterDebounceMs={1000}
                    filterMode="server"
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                    sortingMode="server"
                />
            </Card.Body>
        </Card>
    );
};

export default AuditList;

const cardStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { padding: 0, overflow: 'hidden', width: '100%' };
    const smallerThanSm: SxProps<Theme> = {
        padding: 0,
        overflow: 'hidden',
        borderRadius: 0
    };

    return media ? biggerThanSm : { ...biggerThanSm, ...smallerThanSm };
};
